import axios from 'axios';
import jwt_decode from 'jwt-decode';
import Cookies from 'js-cookie';
import { throwDevError } from 'utilities/ErrorHandlling/ErrorHandlling';
import { apiServices } from 'redux/utils/api_services';
import moment from 'moment';
import { isEmpty as _isEmpty, isUndefined as _isUndefined } from 'lodash-es';
import {
  compressAndStoreInLocalStorage,
  hopperInitialData,
  retrieveAndDecompressFromLocalStorage,
  getStaticConversations
} from 'Modules/Hopper/utility/utility';
import { getIsDemoHost } from 'utilities/demo_utilities';

export default class Api {
  constructor(url = null, service = null, timeout = null) {
    if (!timeout) {
      // ! increasing timeout for AMA API response
      timeout = 150000;
    }

    const axiosCancelToken = axios.CancelToken;
    this.source = axiosCancelToken.source();
    const cancelToken = this.source.token;

    const BASE_URL = `${apiServices[service] || process.env.REACT_APP_API_HOST || 'http://localhost:8000'}/${url}`;

    var instance = axios.create({
      baseURL: BASE_URL,
      responseType: 'json',
      timeout: timeout,
      cancelToken
    });

    const locale = Cookies.get('locale');

    instance.interceptors.request.use(request => {
      request.headers.authType = `graas`;
      request.headers.locale = locale || 'en_US';
      request.headers.countryCode = Cookies.get('countryCode');
      if (!this.isPublicApi(request)) {
        request.headers.Authorization = `Bearer ${this.getAccessToken()}`;
      }
      return request;
    });

    this.instance = instance;
    this.get = this.apiMethodWrapper(this.instance.get);
    this.delete = this.apiMethodWrapper(this.instance.delete);
    this.post = this.apiMethodWrapper(this.instance.post);
    this.put = this.apiMethodWrapper(this.instance.put);
    this.patch = this.apiMethodWrapper(this.instance.patch);
    this.cancel = () => {
      this.source.cancel('User navigated somewhere else.');
    };
  }

  isPublicApi(request) {
    let publicAPI = false;
    if ([].includes(request.url)) {
      publicAPI = true;
    }
    return publicAPI;
  }

  // --- get Access token from cookies
  getAccessToken() {
    const isDemoHost = getIsDemoHost();
    const accessToken = Cookies.get(isDemoHost ? 'demo-accessToken' : 'accessToken');
    if (accessToken) {
      return accessToken;
    } else {
      this.clearAuthCookies();
    }
  }

  // --- get refresh token from cookies
  getRefreshToken() {
    const isDemoHost = getIsDemoHost();
    const refreshToken = Cookies.get(isDemoHost ? 'demo-refreshToken' : 'refreshToken');
    if (refreshToken) {
      return refreshToken;
    } else {
      this.clearAuthCookies();
    }
  }

  // --- get user data from cookies
  getUser() {
    let userData = localStorage.getItem('user_metadata');
    if (userData) {
      userData = JSON.parse(userData);
      return userData;
    } else {
      this.clearAuthCookies();
    }
  }

  // --- Get cookies expiry
  getExpiryOfCookies() {
    const expires = new Date();
    expires.setTime(expires.getTime() + 1 * 24 * 60 * 60 * 1000);
    return expires.toUTCString() || 1;
  }

  // --- Set access token in cookies
  setAccessToken(accessToken) {
    if (accessToken) {
      let prefixData = '';
      const isDemoHost = getIsDemoHost();
      if (isDemoHost) {
        prefixData = 'demo-';
      }
      document.cookie = `${prefixData}accessToken=${accessToken};expires=${this.getExpiryOfCookies()};domain=${
        process.env.NODE_ENV === 'development' ? 'localhost' : process.env.REACT_APP_COOKIE_PATH
      };path=/`;
    }
  }

  // --- Set refresh token in cookies
  setRefreshToken(refreshToken) {
    if (refreshToken) {
      let prefixData = '';
      const isDemoHost = getIsDemoHost();
      if (isDemoHost) {
        prefixData = 'demo-';
      }
      document.cookie = `${prefixData}refreshToken=${refreshToken};expires=${this.getExpiryOfCookies()};domain=${
        process.env.NODE_ENV === 'development' ? 'localhost' : process.env.REACT_APP_COOKIE_PATH
      };path=/`;
    }
  }

  // --- Set user data in cookies
  setUser(userData) {
    if (userData) {
      const accessToken = Cookies.get('accessToken');
      var tokenExp = 0;
      if (accessToken) {
        const decodedToken = jwt_decode(accessToken);
        tokenExp = parseInt(decodedToken?.exp);
      }
      const previous_hopper_init_data = retrieveAndDecompressFromLocalStorage(`hopperHistory-${userData?.merchantID}`);

      // If initial data is not available, we create new hopper initial data.
      // Clear old hopper history if expiry_time is not available.
      // Once the token is updated, the token expiry time will reset, so we reset the hopper data as well.

      if (
        _isEmpty(previous_hopper_init_data) ||
        _isUndefined(previous_hopper_init_data?.expiry_time) ||
        previous_hopper_init_data?.expiry_time !== tokenExp
      ) {
        let session_id = `${userData?.merchantID}_${userData?.userName.split(' ')[0]}_${moment().format(
          'YYYY-MM-DD_HH:mm:ss'
        )}`;
        compressAndStoreInLocalStorage(`hopperHistory-${userData?.merchantID}`, {
          ...hopperInitialData,
          session_id: session_id,
          conversations: getStaticConversations(userData?.userName.split(' ')[0], session_id),
          is_get_started_loading: false,
          expiry_time: tokenExp
        });
      }
      localStorage.setItem('user_metadata', JSON.stringify(userData));
    }
  }

  // --- Get updated refresh token
  async getTokenFromRefresh() {
    const refreshToken = this.getRefreshToken();
    if (refreshToken) {
      try {
        const { data } = await this.instance.post(`${apiServices['auth']}services/authToken/v1`, {
          refreshToken: refreshToken,
          grandType: 'refreshToken'
        });
        this.setAccessToken(data?.accessToken);
        this.instance.defaults.headers.authType = `graas`;
        // this.instance.defaults.headers.Authorization = `Bearer ${this.getAccessToken()}`;
        return true;
      } catch (error) {
        throwDevError('Fail to get access token');
        return false;
      }
    }
    return false;
  }

  // --- Check token expiry
  checkTokenExpiry() {
    const accessToken = this.getAccessToken();
    if (accessToken) {
      const decodedToken = jwt_decode(accessToken);
      var tokenExp = parseInt(decodedToken?.exp);
      if (tokenExp > parseInt(Date.now() / 1000)) {
        return false;
      }
    }
    return true;
  }

  // --- Check refresh / access token is valid or not
  async isTokenAlive() {
    if (this.checkTokenExpiry()) {
      const tokenFetched = await this.getTokenFromRefresh();
      if (!tokenFetched) {
        return false;
      }
    }
    return true;
  }

  // --- Clear cookies related to auth
  clearAuthCookies() {
    Cookies.remove('refreshToken');
    Cookies.remove('accessToken');
    Cookies.remove('userLandedInDefaultPage');
    localStorage.removeItem('user_metadata');
  }

  // --- API Method wrapper
  apiMethodWrapper = func => {
    return async function () {
      const isAuth = await this.isTokenAlive();
      if (!isAuth) {
        window.location.reload();
      }

      if (isAuth) {
        const result = await func.apply(this, arguments);
        return result;
      }
      // } catch (error) {
      //   return error;
      // }
    };
    // return { status: 401 };
  };
}
